import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../../Contexts/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    doc,
    addDoc,
    collection,
    query,
    where,
    onSnapshot,
    updateDoc,
    deleteDoc
} from 'firebase/firestore';
import { db } from '../../../../Services/firebase-config';
import {
    Users,
    BookOpen,
    Plus,
    ArrowLeft,
    CheckCircle,
    Globe,
    Calendar as CalendarIcon
} from 'lucide-react';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import MentorLessonCard from './MentorLessonCard';
import ResourceUploadModal from './ResourceUploadModal';

const AllTeamsResourceManagement = () => {
    const { currentUser, userData } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const [teams, setTeams] = useState(location.state?.teams || []);
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [expandedLesson, setExpandedLesson] = useState(null);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [uploadingToAllTeams, setUploadingToAllTeams] = useState(false);
    const [resources, setResources] = useState(location.state?.resources || []);
    const [isLoading, setIsLoading] = useState(!location.state?.resources);

    useEffect(() => {
        if (!teams || teams.length === 0) {
            navigate('/eventdashboard/mentor-dashboard');
            return;
        }

        // Skip fetching resources if they were already passed from the previous page
        if (location.state?.resources && location.state.resources.length > 0) {
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        let unsubscribe;

        // Set up real-time listener for resources based on role
        const resourcesQuery = userData?.role === 'Administrator'
            ? query(collection(db, 'resources'))
            : query(collection(db, 'resources'), where('mentorId', '==', currentUser.uid));

        unsubscribe = onSnapshot(resourcesQuery, (snapshot) => {
            const resourcesData = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setResources(resourcesData);
            setIsLoading(false);
        });

        return () => {
            if (unsubscribe) {
                unsubscribe();
            }
        };
    }, [teams, navigate, currentUser?.uid, userData?.role, location.state]);

    // Helper function to format date for lessons data lookup
    const formatDateForLookup = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    // Helper function to check if any resources exist on this date
    const hasResourcesOnDate = (date) => {
        if (!resources || resources.length === 0) return false;
        const dateKey = formatDateForLookup(date);
        return resources.some(resource => resource.date === dateKey);
    };

    // Get resources for selected date
    const getResourcesForDate = (date) => {
        if (!resources || resources.length === 0) return [];
        const dateKey = formatDateForLookup(date);
        return resources.filter(resource => resource.date === dateKey)
            .map(resource => ({
                ...resource,
                teamsCount: resource.allowedTeams?.length || 0
            }));
    };

    // Custom tile content to show resource indicators
    const tileContent = ({ date, view }) => {
        if (view === 'month' && hasResourcesOnDate(date)) {
            return <div className="absolute bottom-1 right-1 w-2 h-2 bg-custom-green-1 rounded-full"></div>;
        }
        return null;
    };

    // Custom tile class name for styling
    const tileClassName = ({ date, view }) => {
        if (view === 'month') {
            const today = new Date();
            const isToday = date.toDateString() === today.toDateString();
            const isSelected = date.toDateString() === selectedDate.toDateString();

            if (isSelected) {
                return 'react-calendar__tile--selected';
            }
            if (isToday) {
                return 'react-calendar__tile--today';
            }
            if (hasResourcesOnDate(date)) {
                return 'react-calendar__tile--has-lessons';
            }
        }
        return null;
    };

    const handleResourceDelete = async (resourceId) => {
        try {
            await deleteDoc(doc(db, 'resources', resourceId));

            // Update local state immediately
            setResources(prevResources =>
                prevResources.filter(resource => resource.id !== resourceId)
            );

            setSuccessMessage('Resource deleted successfully.');
            setTimeout(() => setSuccessMessage(''), 3000);
        } catch (error) {
            console.error('Error deleting resource:', error);
            setSuccessMessage('Error deleting resource. Please try again.');
            setTimeout(() => setSuccessMessage(''), 3000);
        }
    };

    const handleResourceUpdate = async (resourceId, updatedData) => {
        try {
            const resourceRef = doc(db, 'resources', resourceId);
            await updateDoc(resourceRef, updatedData);

            // Update local state immediately
            setResources(prevResources =>
                prevResources.map(resource =>
                    resource.id === resourceId
                        ? { ...resource, ...updatedData }
                        : resource
                )
            );

            setSuccessMessage('Resource updated successfully.');
            setTimeout(() => setSuccessMessage(''), 3000);
        } catch (error) {
            console.error('Error updating resource:', error);
            setSuccessMessage('Error updating resource. Please try again.');
            setTimeout(() => setSuccessMessage(''), 3000);
        }
    };

    const handleResourceUpload = async (resourceData) => {
        if (!teams || teams.length === 0) return;

        try {
            setUploadingToAllTeams(true);
            const mentorName = userData?.displayName || currentUser.email || 'Your mentor';

            const newResource = {
                title: resourceData.title,
                instructor: mentorName,
                type: resourceData.type,
                status: 'available',
                description: resourceData.description,
                uploadedAt: new Date(),
                visibility: 'public',
                date: formatDateForLookup(selectedDate),
                ...(userData?.role === 'Administrator'
                    ? {
                        isAdminResource: true
                    }
                    : {
                        mentorId: currentUser.uid,
                        isAdminResource: false
                    }
                ),
                allowedTeams: [],
                ...resourceData.resourceDetails
            };

            // Add new resource to the resources collection
            const docRef = await addDoc(collection(db, 'resources'), newResource);

            // Update local state immediately
            const newResourceWithId = {
                id: docRef.id,
                ...newResource
            };
            setResources(prevResources => [...prevResources, newResourceWithId]);

            // Create notifications for all team members in batches
            const BATCH_SIZE = 100;
            const allMembers = teams.flatMap(team => team.members || []);
            const totalBatches = Math.ceil(allMembers.length / BATCH_SIZE);

            for (let i = 0; i < totalBatches; i++) {
                const start = i * BATCH_SIZE;
                const end = Math.min(start + BATCH_SIZE, allMembers.length);
                const batchMembers = allMembers.slice(start, end);

                const batchPromises = batchMembers.map(member => {
                    const notificationData = {
                        message: `${mentorName} has uploaded a new resource "${resourceData.title}" for all teams. Check it out to enhance your learning experience!`,
                        read: false,
                        timestamp: new Date(),
                        userId: member.id,
                        resourceId: docRef.id
                    };
                    return addDoc(collection(db, 'notifications'), notificationData);
                });

                // Execute batch of notification operations
                await Promise.all(batchPromises);

                // Optional: Add a small delay between batches to prevent rate limiting
                if (i < totalBatches - 1) {
                    await new Promise(resolve => setTimeout(resolve, 500));
                }
            }

            setShowUploadModal(false);

            // Calculate total notifications sent
            const totalMembers = teams.reduce((sum, team) => sum + (team.members?.length || 0), 0);
            setSuccessMessage(`Resource uploaded successfully to ${teams.length} teams! Notifications sent to ${totalMembers} team members.`);
            setTimeout(() => setSuccessMessage(''), 4000);
        } catch (error) {
            console.error('Error uploading resource to all teams:', error);
            setSuccessMessage('Error uploading resource. Please try again.');
            setTimeout(() => setSuccessMessage(''), 3000);
        } finally {
            setUploadingToAllTeams(false);
        }
    };

    const getTotalMembers = () => {
        return teams.reduce((sum, team) => sum + (team.members?.length || 0), 0);
    };

    const getTotalResources = () => {
        return resources.length;
    };

    const getSelectedDateResources = () => {
        const dateKey = formatDateForLookup(selectedDate);
        return resources.filter(resource => resource.date === dateKey);
    };

    if (!teams || teams.length === 0) {
        return (
            <div className="min-h-screen bg-gray-50 p-6">
                <div className="max-w-4xl mx-auto">
                    <div className="text-center py-12">
                        <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                        <h2 className="text-2xl font-bold text-gray-900 mb-2">No Teams Available</h2>
                        <p className="text-gray-600 mb-4">There are no teams available for resource management.</p>
                        <button
                            onClick={() => navigate('/eventdashboard/mentor-dashboard')}
                            className="text-custom-blue-1 hover:text-blue-700 font-medium"
                        >
                            Return to Dashboard
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 p-6">
            <div className="max-w-7xl mx-auto">
                {/* Header with Back Button */}
                <div className="mb-8">
                    <button
                        onClick={() => navigate('/eventdashboard/mentor-dashboard')}
                        className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        Back to Dashboard
                    </button>
                    <div className="flex items-center gap-3 mb-2">
                        <Globe className="w-8 h-8 text-custom-blue-1" />
                        <h1 className="text-3xl font-bold text-gray-900">All Teams Resource Management</h1>
                    </div>
                    <p className="text-gray-600">Upload and manage learning resources for all teams simultaneously</p>
                </div>

                {/* Success Message */}
                {successMessage && (
                    <div className="mb-6 bg-green-50 border border-green-200 rounded-md p-4">
                        <div className="flex">
                            <CheckCircle className="h-5 w-5 text-green-400" />
                            <div className="ml-3">
                                <p className="text-sm font-medium text-green-800">
                                    {successMessage}
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                <div className="space-y-8">
                    {/* Overview Statistics */}
                    <div className="grid md:grid-cols-4 gap-6">
                        <div className="bg-white rounded-xl shadow-sm border p-6">
                            <div className="flex items-center gap-3">
                                <div className="w-12 h-12 bg-custom-blue-1/10 rounded-lg flex items-center justify-center">
                                    <Users className="w-6 h-6 text-custom-blue-1" />
                                </div>
                                <div>
                                    <p className="text-sm text-gray-600">Total Teams</p>
                                    <p className="text-2xl font-bold text-gray-900">{teams.length}</p>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white rounded-xl shadow-sm border p-6">
                            <div className="flex items-center gap-3">
                                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                                    <Users className="w-6 h-6 text-green-600" />
                                </div>
                                <div>
                                    <p className="text-sm text-gray-600">Total Members</p>
                                    <p className="text-2xl font-bold text-gray-900">{getTotalMembers()}</p>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white rounded-xl shadow-sm border p-6">
                            <div className="flex items-center gap-3">
                                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                                    <BookOpen className="w-6 h-6 text-purple-600" />
                                </div>
                                <div>
                                    <p className="text-sm text-gray-600">Total Resources</p>
                                    <p className="text-2xl font-bold text-gray-900">{getTotalResources()}</p>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white rounded-xl shadow-sm border p-6">
                            <div className="flex items-center gap-3">
                                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                                    <CalendarIcon className="w-6 h-6 text-orange-600" />
                                </div>
                                <div>
                                    <p className="text-sm text-gray-600">Resources Today</p>
                                    <p className="text-2xl font-bold text-gray-900">{getSelectedDateResources().length}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Calendar and Resources */}
                    <div className="grid lg:grid-cols-2 gap-8">
                        {/* Calendar */}
                        <div className="bg-white rounded-xl shadow-sm border p-6">
                            <div className="flex items-center justify-between mb-6">
                                <h3 className="text-xl font-bold text-gray-900">Resource Calendar</h3>
                                <button
                                    onClick={() => setShowUploadModal(true)}
                                    disabled={uploadingToAllTeams}
                                    className="flex items-center gap-2 px-4 py-2 bg-custom-blue-1 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <Plus className="w-4 h-4" />
                                    {uploadingToAllTeams ? 'Uploading...' : 'Add Resource for All Teams'}
                                </button>
                            </div>
                            <div className="calendar-container">
                                {isLoading ? (
                                    <div className="animate-pulse">
                                        <div className="h-8 bg-gray-200 rounded mb-4"></div>
                                        <div className="grid grid-cols-7 gap-1">
                                            {[...Array(35)].map((_, index) => (
                                                <div key={index} className="h-12 bg-gray-200 rounded"></div>
                                            ))}
                                        </div>
                                    </div>
                                ) : (
                                    <Calendar
                                        onChange={setSelectedDate}
                                        value={selectedDate}
                                        tileContent={tileContent}
                                        tileClassName={tileClassName}
                                        className="react-calendar-custom"
                                    />
                                )}
                            </div>
                            <div className="mt-4 text-sm text-gray-600">
                                <p>• Green dots indicate days with resources</p>
                                <p>• Resources uploaded here will be added to all {teams.length} teams</p>
                            </div>
                        </div>

                        {/* Resources for Selected Date */}
                        <div className="bg-white rounded-xl shadow-sm border p-6">
                            <h3 className="text-xl font-bold text-gray-900 mb-6">
                                Resources for {selectedDate.toLocaleDateString()}
                            </h3>

                            <div className="space-y-4">
                                {isLoading ? (
                                    // Loading skeleton for resources
                                    [...Array(3)].map((_, index) => (
                                        <div key={index} className="animate-pulse">
                                            <div className="flex items-center space-x-4 p-4 border rounded-lg">
                                                <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
                                                <div className="flex-1 space-y-3">
                                                    <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                                                    <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                ) : getResourcesForDate(selectedDate).length > 0 ? (
                                    getResourcesForDate(selectedDate).map((resource) => (
                                        <div key={resource.id} className="space-y-2">
                                            <MentorLessonCard
                                                lesson={resource}
                                                isExpanded={expandedLesson === resource.id}
                                                onToggle={() => setExpandedLesson(
                                                    expandedLesson === resource.id
                                                        ? null
                                                        : resource.id
                                                )}
                                                onEdit={updatedData => handleResourceUpdate(resource.id, updatedData)}
                                                onDelete={() => handleResourceDelete(resource.id)}
                                                showActions={userData?.role === 'Administrator' || currentUser?.uid === resource.mentorId}
                                            />
                                        </div>
                                    ))
                                ) : (
                                    <div className="text-center py-8">
                                        <BookOpen className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                                        <p className="text-gray-500">No resources uploaded for this date</p>
                                        <button
                                            onClick={() => setShowUploadModal(true)}
                                            className="mt-4 text-custom-blue-1 hover:text-blue-700 font-medium"
                                        >
                                            Upload resource for all teams
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Teams Overview */}
                    <div className="bg-white rounded-xl shadow-sm border p-6">
                        <h3 className="text-xl font-bold text-gray-900 mb-6">Teams Overview</h3>
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {teams.map(team => (
                                <div key={team.id} className="border border-gray-200 rounded-lg p-4 hover:border-custom-blue-1 transition-colors">
                                    <h4 className="font-semibold text-gray-900 mb-2">{team.name}</h4>
                                    <div className="space-y-1 text-sm text-gray-600">
                                        <div className="flex justify-between">
                                            <span>Members:</span>
                                            <span>{team.members?.length || 0}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span>Available Resources:</span>
                                            <span>
                                                {resources.filter(resource =>
                                                    resource.isAdminResource === true ||
                                                    (resource.allowedTeams && resource.allowedTeams.includes(team.id))
                                                ).length}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Upload Modal */}
                {showUploadModal && (
                    <ResourceUploadModal
                        isOpen={showUploadModal}
                        onClose={() => setShowUploadModal(false)}
                        onUpload={handleResourceUpload}
                        selectedDate={selectedDate}
                        isUploadingToAllTeams={true}
                    />
                )}
            </div>
        </div>
    );
};

export default AllTeamsResourceManagement;
